import { ModalInput } from "components/atoms/input/modal/ModalInput";
import { Text, ValidateWord } from "components/atoms/text/Text";
import styled from "styled-components";
import { COLORS } from "styles/colors";
import { SubscriptionCommonWrapper } from "pages/subscription/SubscriptionStyled";
import { FLEX } from "styles/flex";
import {
  KeyCost,
  KeyInfo,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { formatUppercaseOnly } from "utils/functions";
import {
  changeServiceNameUnit,
  showCost,
  useKeyType,
} from "hook/subscription/useKeyType";
import { ReactComponent as WARNING_ICON } from "assets/icons/triangle_warning_red.svg";
import * as mediaQuery from "components/MediaQuery";

type Props = {
  isReplace?: string;
  keyInfo: KeyInfo;
  cost: KeyCost;
  validateMsg: string;
};

export default function ConfirmKeyInfo({
  isReplace,
  keyInfo,
  cost,
  validateMsg,
}: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { durationMonthOptions } = useKeyType();

  return (
    <Wrapper>
      {validateMsg !== "" && (
        <ValidateMsgWrapper>
          <WARNING_ICON />
          <ValidateWord data-testid="subscription-modal-confirm-key-info-validate">
            {validateMsg}
          </ValidateWord>
        </ValidateMsgWrapper>
      )}
      <div className="title-wrapper">
        <Text bold fontSize={26}>
          {isReplace ? "Subscription" : "Confirm"} Key Information
        </Text>
        {isReplace ? (
          <Text color={COLORS.COMPANY}>
            You are allowed one replacement within 30 days. Once replaced, it
            will become active again.
          </Text>
        ) : (
          <Text>
            Review the information below before generating the keys. <br />
            <Text color={COLORS.COMPANY}>
              Once the subscription key(s) is(are) generated, it cannot be
              canceled or deleted.
            </Text>
          </Text>
        )}
      </div>
      {isReplace && (
        <Text bold fontSize={26} className="replace-key-box ">
          {isReplace}
        </Text>
      )}
      <div className="info">
        <ModalInput label="Key Type">
          <Text className="value">{formatUppercaseOnly(keyInfo.keyType)}</Text>
        </ModalInput>
        <ModalInput label="Duration">
          <Text className="value">
            {durationMonthOptions(
              keyInfo.keyType as SUBSCRIPTION_TYPE,
              true
            ).find((opt) => opt.value === String(keyInfo.duration))
              ? durationMonthOptions(
                  keyInfo.keyType as SUBSCRIPTION_TYPE,
                  true
                ).find((opt) => opt.value === String(keyInfo.duration))?.label
              : ""}
          </Text>
        </ModalInput>

        <ModalInput label={changeServiceNameUnit(keyInfo.keyType)}>
          <Text className="value">
            {(keyInfo.number || 0).toLocaleString()}
          </Text>
        </ModalInput>
        {!isReplace && (
          <ModalInput label="Quantity of Keys">
            <Text className="value">{(keyInfo.qty || 0).toLocaleString()}</Text>
          </ModalInput>
        )}

        {selectedAccount.accountLevel === LEVEL_TYPE.DW && (
          <>
            <ModalInput label={"Reference Number"}>
              <div className="value">{keyInfo.reference || "-"}</div>
            </ModalInput>
            <ModalInput label={"Distributor"}>
              <div className="value">{keyInfo.distributor || "-"}</div>
            </ModalInput>
            <ModalInput label={"Price"}>
              <div className="value">
                {keyInfo.isFreeKey ? "Free Keys" : keyInfo.actualPrice || "-"}
              </div>
            </ModalInput>
          </>
        )}

        {!isReplace && selectedAccount.accountLevel === LEVEL_TYPE.CP && (
          <>
            <ModalInput label="Cost Per Month">
              <Text className="value">${showCost(cost.monthlyCost)}</Text>
            </ModalInput>
            <ModalInput label="Total Cost">
              <Text className="value">${showCost(cost.totalCost)}</Text>
            </ModalInput>
          </>
        )}
      </div>
    </Wrapper>
  );
}

const Wrapper = styled(SubscriptionCommonWrapper)`
  padding: 20px 80px;

  ${mediaQuery.isMobile} {
    padding: 20px;
  }

  .title-wrapper {
    width: 100%;

    ${FLEX.FlexStartStart};
    flex-direction: column;
    gap: 10px;

    * {
      white-space: pre-wrap;
    }
  }

  .info {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    row-gap: 20px;
  }

  .modal-input-label-title {
    color: ${COLORS.BUTTONS1};
  }

  .replace-key-box {
    border-radius: 8px;
    border: 1px solid ${COLORS.BORDER};
    background: rgba(217, 217, 217, 0.08);
    ${FLEX.FlexCenterCenter};
    padding: 20px 0;
    margin-top: 30px;
  }
  .value {
    word-break: break-word;
    overflow-wrap: break-word;
    color: ${({ theme }) => theme.COLORS.WHITE};
  }
`;

export const ValidateMsgWrapper = styled.div`
  padding: 20px;
  background-color: ${COLORS.VALIDATERED}1A;
  margin-bottom: 20px;
  ${FLEX.FlexStartCenter};
  gap: 10px;
  border: 1px solid ${COLORS.VALIDATERED};

  .validate-word {
    padding: 0;
  }
`;
